import React, { useEffect, useState } from 'react'
import { Label } from 'semantic-ui-react'
import { ChatsCircle } from 'phosphor-react'
import { useNavigate } from 'react-router-dom'
import { QuestionThreadService } from 'utility/services/question-threads'
import {
    formatThreadTime,
    inspectionRouteFor,
    normalizeWaitingItems,
    statusInfo,
    waitingForText,
} from 'utility/question-threads'

const SHOWN = 5
const WAITING_COLOR = { reply: 'red', review: 'orange', 'finish-review': 'blue' }

/**
 * Task list: "Comments waiting for you" (GET Threads/WaitingOnMe) - department users see Open threads to reply to,
 * reviewers see Replied threads to review. Each row opens the inspection at that question. Hidden when empty or when
 * the route is not there (404 / 503).
 */
export const WaitingCommentsStrip = ({ refreshKey }) => {
    const navigate = useNavigate()
    const [items, setItems] = useState([])
    const [showAll, setShowAll] = useState(false)

    useEffect(() => {
        let alive = true
        QuestionThreadService.waitingOnMe(50)
            .then(response => alive && setItems(normalizeWaitingItems(response)))
            .catch(() => alive && setItems([]))
        return () => {
            alive = false
        }
    }, [refreshKey])

    if (!items.length) return null
    const rows = showAll ? items : items.slice(0, SHOWN)

    const open = item => {
        const route = inspectionRouteFor(item)
        if (!route) return
        // "finish-review" rows are the whole inspection: no question to scroll to
        if (item.WaitingFor === 'finish-review' || !item.AuditQuestionId) navigate(route)
        else navigate(route, { state: { focusQuestionId: item.AuditQuestionId } })
    }

    return (
        <div
            style={{
                margin: '-8px 0 16px',
                border: '1px solid #c7cdf5',
                background: '#f5f7ff',
                borderRadius: 8,
                padding: '10px 14px',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, marginBottom: 6 }}>
                <ChatsCircle size={18} />
                Comments waiting for you
                <Label size="tiny" circular color="blue">
                    {items.length}
                </Label>
            </div>
            {rows.map(item => {
                const info = statusInfo(item.Status)
                const since = formatThreadTime(item.WaitingSinceUtc || item.LastActivityOnUtc)
                return (
                    <div
                        key={item.ThreadId > 0 ? item.ThreadId : `review-${item.AuditId || item.AuditGuid}`}
                        role="button"
                        tabIndex={0}
                        className="pointer"
                        onClick={() => open(item)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') open(item)
                        }}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            flexWrap: 'wrap',
                            padding: '6px 0',
                            borderTop: '1px solid #e0e4fa',
                            cursor: 'pointer',
                        }}
                    >
                        <Label size="tiny" color={WAITING_COLOR[item.WaitingFor] || 'red'} title={item.ThreadId > 0 ? info.hint : ''}>
                            {waitingForText(item.WaitingFor)}
                        </Label>
                        <span style={{ fontWeight: 600 }}>{item.AuditName || 'Inspection'}</span>
                        {item.TemplateName && <span style={{ color: '#6b7280' }}>{item.TemplateName}</span>}
                        <span
                            style={{
                                flex: '1 1 240px',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: '#374151',
                            }}
                            title={item.QuestionText}
                        >
                            {item.WaitingFor === 'finish-review' ? 'Completed - mark the review done' : item.QuestionText}
                        </span>
                        {since && <span style={{ color: '#6b7280', fontSize: 12 }}>since {since}</span>}
                    </div>
                )
            })}
            {items.length > SHOWN && (
                <button
                    type="button"
                    onClick={() => setShowAll(v => !v)}
                    style={{ border: 'none', background: 'none', padding: 0, marginTop: 4, color: '#1b2587', cursor: 'pointer', fontWeight: 600 }}
                >
                    {showAll ? 'Show fewer' : `Show all ${items.length}`}
                </button>
            )}
        </div>
    )
}
